import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SavedSearchModel } from '@supply-mover-2-0/core';
import clsx from 'clsx';
import type { ForwardedRef, ReactElement } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from '../../../providers/TranslationProvider';

interface SavedSearchButtonProps {
	selectedSearch: SavedSearchModel | null;
	isOpen: boolean;
	onClick: () => void;
}

// forwardRef so the trigger button can serve as the anchor for usePopover,
// which renders the dropdown in a body-level portal (escaping the toolbar/nav
// clip). The ref points at the underlying <button> element.
function SavedSearchButtonInner(
	{ selectedSearch, isOpen, onClick }: SavedSearchButtonProps,
	ref: ForwardedRef<HTMLButtonElement>,
): ReactElement {
	const { t } = useTranslation();

	const isActive = selectedSearch != null;
	const displayText = isActive
		? `${t('Saved Search')}: ${t(selectedSearch.name)}`
		: t('Saved Searches');

	return (
		<button
			ref={ref}
			type="button"
			onClick={onClick}
			className={clsx(
				'inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md',
				'text-xs transition-all min-h-[36px]',
				{
					// Active state (saved search selected)
					'bg-surface shadow-sm border-b-2 border-accent': isActive,
					'font-medium text-default': isActive,
					// Default state
					'bg-surface shadow-sm': !isActive,
					'text-muted': !isActive,
				},
			)}
		>
			<span className="truncate max-w-[200px]">{displayText}</span>
			<FontAwesomeIcon
				icon={faChevronDown}
				className={clsx('text-xs transition-transform', {
					'rotate-180': isOpen,
					'text-default': isActive,
					'text-muted': !isActive,
				})}
			/>
		</button>
	);
}

export const SavedSearchButton = forwardRef(SavedSearchButtonInner);
